import { useState } from "react";
import { useT } from "@/i18n";

// Circle of fifths: clockwise adds one sharp, counter-clockwise adds one flat.
const CIRCLE = [
  { major: "C", minor: "Am", type: "n", count: 0, acc: [] },
  { major: "G", minor: "Em", type: "s", count: 1, acc: ["F#"] },
  { major: "D", minor: "Bm", type: "s", count: 2, acc: ["F#", "C#"] },
  { major: "A", minor: "F#m", type: "s", count: 3, acc: ["F#", "C#", "G#"] },
  { major: "E", minor: "C#m", type: "s", count: 4, acc: ["F#", "C#", "G#", "D#"] },
  { major: "B", minor: "G#m", type: "s", count: 5, acc: ["F#", "C#", "G#", "D#", "A#"] },
  { major: "F#/Gb", minor: "D#m/Ebm", type: "b", count: 6, acc: ["F#", "C#", "G#", "D#", "A#", "E#"] },
  { major: "Db", minor: "Bbm", type: "b", count: 5, acc: ["Bb", "Eb", "Ab", "Db", "Gb"] },
  { major: "Ab", minor: "Fm", type: "b", count: 4, acc: ["Bb", "Eb", "Ab", "Db"] },
  { major: "Eb", minor: "Cm", type: "b", count: 3, acc: ["Bb", "Eb", "Ab"] },
  { major: "Bb", minor: "Gm", type: "b", count: 2, acc: ["Bb", "Eb"] },
  { major: "F", minor: "Dm", type: "b", count: 1, acc: ["Bb"] },
];

const CX = 200;
const CY = 200;
const pos = (i, r) => {
  const theta = ((i * 30 - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(theta), y: CY + r * Math.sin(theta) };
};

export const CircleOfFifths = () => {
  const { t } = useT();
  const [selected, setSelected] = useState(0);
  const active = CIRCLE[selected];

  const sigText = (k) => {
    if (k.count === 0) return t("circle.none");
    if (k.major === "F#/Gb") {
      return `${t("circle.either")} 6 ${t("circle.sharps")}: F#, C#, G#, D#, A#, E# ${t("circle.or")} 6 ${t("circle.flats")}: Bb, Eb, Ab, Db, Gb, Cb`;
    }
    const word = k.type === "s"
      ? t(k.count === 1 ? "circle.sharp1" : "circle.sharps")
      : t(k.count === 1 ? "circle.flat1" : "circle.flats");
    return `${k.count} ${word}: ${k.acc.join(", ")}`;
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="circle-of-fifths">
      <div className="relative">
        <svg viewBox="0 0 400 400" className="w-full max-w-[440px] mx-auto">
          <circle cx={CX} cy={CY} r={188} fill="#FDFBF7" stroke="#0a0a0a" strokeWidth="3" />
          <circle cx={CX} cy={CY} r={128} fill="none" stroke="#0a0a0a" strokeWidth="2" />
          <circle cx={CX} cy={CY} r={72} fill="#0a0a0a" />
          {CIRCLE.map((_, i) => {
            const a = pos(i, 188);
            return (
              <line key={`l-${i}`} x1={CX} y1={CY} x2={a.x} y2={a.y} stroke="#0a0a0a" strokeWidth="1" opacity="0.18" />
            );
          })}

          <text x={CX} y={CY - 6} textAnchor="middle" fill="#FDFBF7" fontFamily="Outfit" fontWeight="800" fontSize="14">
            {t("circle.centerA")}
          </text>
          <text x={CX} y={CY + 14} textAnchor="middle" fill="#66AEFF" fontFamily="Outfit" fontWeight="800" fontSize="14">
            {t("circle.centerB")}
          </text>

          {CIRCLE.map((k, i) => {
            const maj = pos(i, 158);
            const min = pos(i, 100);
            const isActive = i === selected;
            const fill = k.type === "s" ? "#66AEFF" : k.type === "b" ? "#FF7A70" : "#66AEFF";
            return (
              <g key={k.major} onClick={() => setSelected(i)} style={{ cursor: "pointer" }}>
                <circle cx={maj.x} cy={maj.y} r={20} fill={isActive ? fill : "#FFFFFF"} stroke="#0a0a0a" strokeWidth={isActive ? 3 : 2} />
                <text x={maj.x} y={maj.y + 5} textAnchor="middle" fontFamily="Outfit" fontWeight="800" fontSize={k.major.length > 2 ? 9 : 14} fill={isActive && k.type !== "n" ? "#FFFFFF" : "#0a0a0a"}>
                  {k.major}
                </text>
                <text x={min.x} y={min.y + 4} textAnchor="middle" fontFamily="Outfit" fontWeight="700" fontSize={k.minor.length > 3 ? 8 : 11} fill={isActive ? "#66AEFF" : "#FDFBF7"} opacity={isActive ? 1 : 0.7}>
                  {k.minor}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="flex flex-wrap justify-center gap-4 mt-4 text-xs font-semibold">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: "#66AEFF" }} /> {t("circle.right")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: "#FF7A70" }} /> {t("circle.left")}
          </span>
        </div>
      </div>

      <div className="bg-background text-foreground brutal-border rounded-2xl p-7" style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }} data-testid="circle-detail">
        <div className="flex items-baseline gap-3">
          <h3 className="font-display font-extrabold text-3xl tracking-tight">{active.major} {t("circle.major")}</h3>
          <span className="text-sm font-semibold text-muted-foreground">{t("circle.rel")} {active.minor}</span>
        </div>
        <p className="mt-2 font-display font-bold text-lg" style={{ color: active.type === "b" ? "#FF7A70" : active.type === "s" ? "#66AEFF" : "#0a0a0a" }}>
          {sigText(active)}
        </p>
        {active.acc.length > 0 ? (
          <div className="mt-4">
            <p className="step-tag text-muted-foreground mb-2">{t("circle.theAcc")}</p>
            <div className="flex flex-wrap gap-2">
              {active.acc.map((a) => (
                <span key={a} className="font-display font-bold text-sm px-3 py-1.5 rounded-lg brutal-border bg-muted">{a}</span>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">{t("circle.homeText")}</p>
        )}
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{t("circle.explain")}</p>
        <div className="mt-4 bg-muted rounded-xl p-4 border-2 border-foreground">
          <p className="text-sm leading-relaxed">{t("circle.flatSharp")}</p>
        </div>
      </div>
    </div>
  );
};
